const component = require('../component/component');

// A "surface" is a host that a popupEdit renders its remotely-fetched components and
// actions into. Two hosts implement this contract: `modal` (body-level dialog) and
// `inlineSurface` (a block dropped into a `container` component on the page).
//
// The contract popupEdit drives:
//   element        the surface root; everything it hosts lives under it
//   formElement    the <form> that OWNS the hosted components (see below)
//   initForm()     name that form — must run BEFORE hm.app.initComponents()
//   setTitle() / appendContent()
//   positions / addActions() / actionsParent()
//   stampFormId() / watchActions()
//   open() / close() / dispose() / needsRebuild()
//
// Form ownership. A surface never nests its <form> inside another one. The modal's
// form comes from its own body-level template; the inline surface creates an EMPTY
// body-level form and points its controls at it with the html `form` attribute (which,
// per the form-owner algorithm, overrides the nearest-ancestor-form rule). Either way
// the hosted components carry data-zit-form-id = the surface's form id, which is what
// action.formComponents('form') filters the component registry by — so the surface's
// submit actions post the surface's components and nothing else.
const surface = Object.assign({}, component, {
    // The <form> that owns everything this surface hosts.
    formElement: null,
    // Action zones the leaf template exposes via data-zit-actions-position.
    positions: [],
    // Zone actions land in when addActions() is called without a position.
    defaultPosition: false,
    disposed: false,

    // Name the surface's form. The form must exist and carry this id before the hosted
    // controls are inited: a control whose `form` attribute names no form element has a
    // null form owner and is silently never submitted.
    initForm: function (formId, action, method, enctype) {
        if (this.formElement === null) {
            return;
        }

        this.formElement.id = formId;
        this.formElement.action = typeof action === typeof undefined ? '' : action;
        this.formElement.method = typeof method === typeof undefined ? 'POST' : method;
        this.formElement.enctype = typeof enctype === typeof undefined ? 'multipart/form-data' : enctype;
    },

    // Tag every component the surface hosts with the surface's form id, so
    // component.readZAttributes() adopts it as its formId instead of inferring one from
    // the closest ancestor <form> (which, for an inline surface, is the host page's
    // mainForm). Run after the content and the actions are in place, before
    // hm.app.initComponents().
    stampFormId: function (formId, root) {
        if (typeof root === typeof undefined) {
            root = this.element;
        }

        if (!formId || root === null) {
            return;
        }

        root.querySelectorAll('[data-zit-component]').forEach((componentElement) => {
            componentElement.dataset.zitFormId = formId;
        });

        // querySelectorAll does not descend into a <template>'s content (it is a detached
        // fragment), and editList keeps its add-row's sample components in one — stamp those
        // too, or a row added at runtime would inherit the host page's form.
        root.querySelectorAll('template').forEach((templateElement) => {
            this.stampFormId(formId, templateElement.content);
        });
    },

    setTitle: function (title) {
        if (typeof this.subelements === typeof undefined || !this.subelements.title) {
            return;
        }

        this.subelements.title.innerHTML = title;
    },

    appendContent: function (contentElement) {
        this.subelements.body.appendChild(contentElement);
    },

    setContent: function (innerHTML) {
        this.subelements.body.innerHTML = innerHTML;
    },

    // The zone a named position resolves to in this surface's template.
    zoneElement: function (position) {
        return this.element.querySelector('[data-zit-actions-position="' + position + '"]');
    },

    // Where a declared-but-absent zone falls back to. Null here: drop the actions rather
    // than land them somewhere arbitrary. (modal falls back to its footer.)
    defaultZoneElement: function () {
        return null;
    },

    // Resolve where a position's actions go. A position naming one of this surface's own
    // zones returns that zone's element. Any other position is the html id of a container
    // component inside the surface's content — the caller resolves the id (see popupEdit,
    // which prefixes it with the form id it just generated). An already-inited container is
    // returned as the component (it owns its own reveal); one not registered yet — the usual
    // case, since actions are placed before the surface is inited — is returned as its
    // element. A position matching no zone and no container resolves to null.
    actionsParent: function (position = false) {
        if (position === false) {
            position = this.defaultPosition;
        }

        if (this.positions.indexOf(position) !== -1) {
            let zoneElement = this.zoneElement(position);

            return zoneElement !== null ? zoneElement : this.defaultZoneElement();
        }

        let containerComponent = this.hm.app.getComponent(position);

        if (containerComponent !== false && typeof containerComponent.addElement !== typeof undefined) {
            return containerComponent;
        }

        return this.element.querySelector('[data-zit-component="container"][id="' + position + '"]');
    },

    // actions: an array or an object of actions (element, HTML string or component).
    addActions: function (actions, position = false) {
        if (this.hm.helper.isArray(actions)) {
            actions.forEach((action) => {
                this.addAction(action, position);
            });
        } else if (this.hm.helper.isObject(actions)) {
            for (let actionName in actions) {
                this.addAction(actions[actionName], position);
            }
        }
    },

    addAction: function (action, position = false) {
        let parent = this.actionsParent(position);

        if (parent === null) {
            return;
        }

        // A container component owns its own reveal; a zone element takes the action
        // straight. Neither inits it — the caller inits the whole surface.
        if (typeof parent.addElement !== typeof undefined) {
            parent.addElement(action, true);
            return;
        }

        this.hm.app.addAction(action, parent, true);

        // An uninited container element (see actionsParent) still renders hidden: reveal it
        // here, since there is no component object to do it.
        if (parent.dataset.zitComponent === 'container') {
            this.hm.helper.showElement(parent);
        }
    },

    // Listen for a hosted action finishing a silent (XHR) request. An action marked
    // extra.closeOnDone closes the surface once its request came back without a redirect
    // (a redirect navigates away, so there is nothing left to close). A loud action never
    // gets here — it submits the form natively and the page reloads.
    watchActions: function () {
        if (this.element === null) {
            return;
        }

        this.element.addEventListener('zitActionDone', (event) => {
            this.onActionDone(event);
        });
    },

    onActionDone: function (event) {
        let doneAction = event.detail.action;
        let response = event.detail.response;

        if (!doneAction || doneAction.extraAttributes.closeOnDone !== true) {
            return;
        }

        // The event bubbles, and surfaces nest (an inline surface opened from inside a modal),
        // so only the surface the action actually belongs to may act on it — otherwise the
        // inline surface's save would close the modal hosting it too.
        if (this.formElement === null || doneAction.formId !== this.formElement.id) {
            return;
        }

        if (response && typeof response._redirect !== typeof undefined) {
            return;
        }

        this.close();
    },

    // True when the surface can no longer be re-opened as-is and popupEdit must rebuild it
    // (an inline surface whose container was cleared out from under it).
    needsRebuild: function () {
        return this.disposed;
    },

    open: function () {
    },

    close: function () {
    },

    dispose: function () {
        this.disposed = true;
    }
});

module.exports = surface;
